import AddIcon from '@atlaskit/icon/glyph/add';
import { Pressable, xcss } from '@atlaskit/primitives';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { REDUCED_POPUP_OFFSET } from '@databases/atlaskit/Popup';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { useComponent } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { createField } from '@databases/utils/database/utils';
import React, { ComponentType, FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';
import FieldTypeDropdown, {
  FieldTypeTriggerProps,
} from '../../database-field/FieldTypeDropdown';
import { FIELD_TYPES, FieldTypeCategory } from '../../field-types/fieldTypes';
export const AddFieldButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  > * {
    width: 100%;
    flex-grow: 1;
  }
`;

export interface AddFieldButtonProps {
  contentId: string;
  currentViewId: string;
  triggerWrapper?: ComponentType;
  isDisabled?: boolean;
}

/**
 * Renders a button that will add a field to a database. The database update is drafted automatically on click.
 */
const AddFieldButton: FunctionComponent<AddFieldButtonProps> = ({
  contentId,
  currentViewId,
  isDisabled,
}) => {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const { showPanel } = usePanel();
  const scope = useScope();
  const client = useAnalytics();

  const { formatMessage } = useIntl();

  const handleClick = useCallback(
    async (type: DatabaseFieldType) => {
      dispatch(deselectAll());
      const field = createField(type);
      store.dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.ADD_FIELD,
          field,
          currentViewId,
        })
      );
      client.sendCreateDatabaseFieldEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'databaseCreateFieldDialog',
      });
      client.sendDatabaseFieldOptionUpdatedEvent({
        source: 'database',
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        fieldSelection: type,
        isNewField: true,
      });

      if (FIELD_TYPES[type].category !== FieldTypeCategory.BASIC) {
        showPanel('FieldCreatePanel', {
          type,
          field,
        });
      }
    },
    [dispatch, store, contentId, scope, currentViewId, client, showPanel]
  );

  const pressableStyles = xcss({
    borderRadius: '0 2px 0 0',
    width: '100%',
    height: '100%',
    color: 'color.text',
    backgroundColor: 'elevation.surface',
    ':active': {
      backgroundColor: 'elevation.surface.hovered',
      color: 'color.text.accent.blue',
    },
  });
  const pressableStylesClosed = xcss({
    ':hover': {
      backgroundColor: 'elevation.surface.hovered',
    },
  });

  const pressableStylesDisabled = xcss({
    color: 'color.text.subtlest',
    ':hover': {
      color: 'color.text.subtlest',
      backgroundColor: 'elevation.surface',
    },
    ':active': {
      color: 'color.text.subtlest',
      backgroundColor: 'elevation.surface',
    },
  });

  const pressableStylesOpen = xcss({
    ':hover': {
      backgroundColor: 'color.background.accent.blue.subtlest.hovered',
    },
    backgroundColor: 'elevation.surface',
    color: 'color.text.accent.blue',
  });

  const trigger = useComponent(
    ({ ref, ...props }: FieldTypeTriggerProps) => (
      <SkeletonItem isButton height={20} width={20}>
        {/* @ts-ignore - wrong types coming from Atlaskit */}
        <Pressable
          testId="add-field-button"
          role="button"
          xcss={[
            pressableStyles,
            props['aria-expanded'] ? pressableStylesOpen : pressableStylesClosed,
            isDisabled && pressableStylesDisabled,
          ]}
          ref={ref}
          isDisabled={isDisabled}
          {...props}
        >
          <AddIcon
            label={formatMessage(messages['table-layout.add-field-label'])}
            size="small"
          />
        </Pressable>
      </SkeletonItem>
    ),
    [
      formatMessage,
      isDisabled,
      pressableStyles,
      pressableStylesClosed,
      pressableStylesDisabled,
      pressableStylesOpen,
    ]
  );

  return (
    <AddFieldButtonContainer>
      <FieldTypeDropdown
        trigger={trigger}
        onChange={handleClick}
        placement="bottom-end"
        offset={REDUCED_POPUP_OFFSET}
      />
    </AddFieldButtonContainer>
  );
};

export default AddFieldButton;
